import { useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "~/constants/messages";

export type ApplyResult =
  | { kind: "success" }
  | { kind: "lock_failed" }
  | { kind: "save_failed" }
  | { kind: "refresh_failed" }
  | { kind: "unlock_failed_after_save" };

export interface DocumentDiffApplyConfig {
  acquireLock: () => Promise<unknown>;
  releaseLock: () => Promise<unknown>;
  saveContent: (content: string) => Promise<unknown>;
  refetch: () => Promise<unknown>;
  setIsDiffSaving: (saving: boolean) => void;
}

/**
 * diff適用後にモーダルを閉じるべきかどうかを判定する。
 * success / refresh_failed はどちらも保存自体は成功しているため閉じる。
 * lock_failed / save_failed / unlock_failed_after_save はdiffを再試行できるため閉じない。
 */
export function shouldCloseDiffModal(result: ApplyResult): boolean {
  return result.kind === "success" || result.kind === "refresh_failed";
}

/**
 * ドキュメントの差分適用フローを共通化するカスタムフック。
 * acquireLock → saveContent → releaseLock → refetch の順で実行する。
 */
export function useDocumentDiffApply(
  config: DocumentDiffApplyConfig,
): (approvedDocument: string) => Promise<ApplyResult> {
  // 最新のconfigを常に参照するためにrefを使用（stale closure防止）
  const configRef = useRef(config);
  configRef.current = config;

  return useCallback(async (approvedDocument: string): Promise<ApplyResult> => {
    const { acquireLock, releaseLock, saveContent, refetch, setIsDiffSaving } = configRef.current;
    setIsDiffSaving(true);
    let phase: "none" | "locked" | "saved" = "none";
    try {
      await acquireLock();
      phase = "locked";
      await saveContent(approvedDocument);
      phase = "saved";

      let unlockFailed = false;
      try {
        await releaseLock();
      } catch {
        unlockFailed = true;
        toast.error(TOAST_MESSAGES.DOCUMENT.UNLOCK_FAILED);
      }

      try {
        await refetch();
      } catch {
        toast.error(TOAST_MESSAGES.DOCUMENT.REFRESH_FAILED);
        if (unlockFailed) {
          return { kind: "unlock_failed_after_save" };
        }
        return { kind: "refresh_failed" };
      }

      if (unlockFailed) {
        return { kind: "unlock_failed_after_save" };
      }
      return { kind: "success" };
    } catch {
      if (phase === "none") {
        toast.error(TOAST_MESSAGES.DRAWIO.LOCK_FAILED);
        return { kind: "lock_failed" };
      }
      // phase === "locked": save失敗
      toast.error(TOAST_MESSAGES.DOCUMENT.SAVE_FAILED);
      return { kind: "save_failed" };
    } finally {
      if (phase === "locked") {
        // save失敗時のクリーンアップ: ロックを解放する（失敗しても無視）
        await releaseLock().catch(() => {});
      }
      setIsDiffSaving(false);
    }
  }, []); // configRefを通じて常に最新値を参照するため依存なし
}
